import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Lottie from 'lottie-react';
import AnalyticsAnimation from '../components/animations/Analytics';
import FadeIn from '../components/animations/FadeIn';
import Button from '../components/Button';
import ContentBlock from '../components/ContentBlock';
import ContentRotator from '../components/ContentRotator';
import homepage from '../components/animations/homepage.json';
import DefaultLayout from '../layouts';

export function Word() {
  const words = ['Imagine', 'Create', 'Customize', 'Brand', 'Automate'];
  const [active, setActive] = useState(0);
  const [width, setWidth] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const next = () => {
    if (window.innerWidth < 1024) {
      return;
    }
    setActive((a) => {
      if (a === words.length - 1) return 0;
      return a + 1;
    });
  };
  useEffect(() => {
    const interval = setInterval(next, 3000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    setTimeout(() => {
      setRefresh((r) => !r);
    }, 2000);
  }, []);
  const isActive = refresh || active;
  return (
    <span
      style={{
        width: !isActive ? 'auto' : width + 15,
        marginRight: isActive ? -15 : '',
        display: 'inline-block',
        transition: 'width 1s',
        position: 'relative',
        overflow: 'hidden',
        verticalAlign: 'bottom',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{ width: 'fit-content', display: 'inline-block' }}
        ref={(el) => {
          if (!el) {
            return;
          }
          const w = el.getBoundingClientRect().width;
          if (width !== w) {
            setWidth(w);
          }
        }}
      >
        {words[active]}
      </span>
    </span>
  );
}
const quotes = [
  {
    quote:
      'The most significant benefit of using Paperform has been the possibility to innovate quickly, creating new pages and types of content without the need to include a tech team or develop a whole set of tools and options. We have been able to create a bunch of new products and showcase new ideas thanks to Paperform. This includes a whole interview that was conducted and published from start-to-finish using Paperform.',
    name: 'Garbiñe Hoyo Martínez',
    business: 'ReasonWhy',
    avatar: '/website/images/reason-why.jpeg',
  },
  {
    quote:
      'Paperform seamlessly matches the look and feel of my existing landing pages. With every other form builder I’ve tried, you can tell that the form has just been embedded onto the website. With Paperform, however, my forms look like they’re part of the website; they just blend into the page and represent my brand perfectly.',
    name: 'Kassy Pajarillo',
    business: 'kassypajarillo.com',
    avatar: '/website/images/kassy.jpg',
  },
  {
    quote:
      'Ecommerce conversion rates tend to average around 2% on mobile devices... Our campaign managed to overcome this, as we reached a staggering 10% conversion rate on our Paperform store. This was largely due to Paperform’s extensive theming options, which allowed us to create a beautiful and professional-looking store that not only instilled trust in those who landed on it, but also helped translate Blütenträume’s physical brand into its online one.',
    name: 'Dennis Karle',
    business: 'Blütenträume',
    avatar: '/website/images/dennis-karle.1024x1024.jpg',
  },
  {
    quote: `My clients have to collect a lot of information from our forms based on the customer’s requests, questions and to allow images to be uploaded. Their previous forms had long interfaces, so they had high abandonment rates…After switching to Paperform there have been no customer hiccups or mistakes; we've maintained a 100% form completion rate.`,
    name: 'Chris Andrade',
    business: 'CJ Andrade Web Design',
    avatar: '/website/images/CJ.jpeg',
  },
  {
    quote:
      'Guided Mode allows our customers to only see one question at a time, making our forms more approachable and interactive. We AB tested our Paperform with our old form and found that our conversion rate increased by a staggering 91%.',
    name: 'Amadeusz Bathelt',
    business: 'Dealavo',
    avatar: '/website/images/dealavo.jpeg',
  },
];

export default function RebrandHomePage(props) {
  return (
    <div className="bg-white w-full h-full">
      <DefaultLayout location={props.location} noFade fixedNav>
        <Helmet titleTemplate={false}>
          <title>One product, limitless potential | Paperform</title>
          <meta
            name="description"
            content=" Payments. Bookings. Events. Onboarding. Calculations. Get paid, stay on schedule, connect with your customers and simplify work through automation—all in one place, and without writing a line of code."
          />
          <meta
            property="og:title"
            content="One product, limitless potential | Paperform"
          />
          <meta
            property="og:description"
            content=" Payments. Bookings. Events. Onboarding. Calculations. Get paid, stay on schedule, connect with your customers and simplify work through automation—all in one place, and without writing a line of code."
          />
          <meta
            name="ahrefs-site-verification"
            content="7d9b940f7eafcfe2db327812738cbb7daad95091a696eec6f3fa5bdc4e059e90"
          />
        </Helmet>

        <div className="bg-cloud">
          <div className="bg-dusk pb-12 lg:bg-cloud bg-left bg-none lg:bg-[url(/website/logo-v3/graphics/1-4.svg)] bg-cover bg-top xl:bg-right">
            <div className="lg:min-h-[calc(100vh-50px)] 3xl:h-[900px] lg:max-h-[1024px] max-width !px-[50px] lg:grid lg:grid-cols-2 items-center">
              <div className="lg:p-[50px] lg:py-[140px] pt-[140px] flex-1 text-black relative">
                <h1 className="text-3xl lg:text-4xl lg:leading-[1.15]">
                  Built to be limitless.
                  <br />
                  Your solutions, your way.
                </h1>
                <p className="max-w-full  text-sm lg:mt-8 lg:w-[400px]">
                  Payments. Bookings. Events. Onboarding. Calculations. Get
                  paid, stay on schedule, connect with your customers and
                  simplify work through automation—all in one place, and without
                  writing a line of code.
                </p>
                <div className="hidden lg:block">
                  <Button gold solid href="/register">
                    Try Now
                  </Button>
                </div>
                <div className="lg:hidden">
                  <Button black solid xl href="/register">
                    Try Now
                  </Button>
                </div>
                <p className="text-xs opacity-50 my-3">
                  Free for 7 days. No credit card needed.
                </p>
              </div>
              <div className="flex items-center justify-center py-6 lg:pt-24">
                <video
                  autoPlay
                  loop
                  muted
                  poster="/website/video/promo-poster.jpg"
                  disableRemotePlayback="true"
                  playsInline
                  style={{ aspectRatio: '1' }}
                  className="w-full max-w-full h-full lg:ml-[-50px] lg:w-[650px] lg:max-w-[50vw]  bg-white border-[10px] border-white lozad rounded-3xl"
                >
                  <source data-src="/website/video/promo.mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-stone">
          <div className="max-width mx-auto text-center">
            <div className="py-12 pt-24">
              <FadeIn>
                <h2 className="mb-12 text-3xl">How it works.</h2>
              </FadeIn>
              <div className="lg:grid lg:grid-cols-4 lg:gap-16 lg:mx-12">
                {[
                  {
                    title: 'Design',
                    image:
                      '/website/logo-v3/icons/Paperform_Icon_Beautiful_Brandable_Black.svg',
                    description:
                      'Start with a template or build a solution in minutes. No code, no worries.',
                  },
                  {
                    title: 'Integrate',
                    image:
                      '/website/logo-v3/icons/Paperform_Icon_Easy_Learn_Black.svg',
                    description:
                      'Connect with the apps you already use to automate work and make life easier.',
                  },
                  {
                    title: 'Share',
                    image:
                      '/website/logo-v3/icons/Paperform_Icon_Easy_Master_Black.svg',
                    description:
                      'Embed on your website, add to an email, or just share your custom link.',
                  },
                  {
                    title: 'Analyse',
                    image: '/website/logo-v3/icons/paperform_analyse.svg',
                    description:
                      'See what works, generate reports and send data wherever you need it to go.',
                  },
                ].map((item, i) => (
                  <div key={i}>
                    <FadeIn>
                      <div
                        className="bg-contain h-[192px] w-[192px] lg:mb-3 mx-auto"
                        style={{ backgroundImage: `url(${item.image})` }}
                      />
                    </FadeIn>
                    <h3 className="text-xl">{item.title}</h3>
                    <p className="mb-[50px] text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white pb-24">
          <ContentBlock
            title="Beautiful design made easy"
            description={
              <span>
                <em>
                  No more suffering through drag and lag interfaces and clunky
                  menus.
                </em>{' '}
                Our intuitive editor is built for anyone (yep, even your
                grandma.) It gets out of your way and makes it easy to design
                beautiful, branded forms in the time it takes to make a cup of
                coffee. As simple as writing a document, and waaay more fun.{' '}
              </span>
            }
          >
            <video
              autoPlay
              loop
              muted
              disableRemotePlayback="true"
              playsInline
              style={{ aspectRatio: '2340 / 1414' }}
              className="w-full max-w-full bg-white my-6 lozad border-[10px] border-stone rounded-xl"
            >
              <source data-src="/website/video/real-form-smaller.mp4" />
            </video>
          </ContentBlock>
          <ContentBlock
            align="right"
            title="More than just a pretty face"
            description={
              <span>
                <em>
                  Transform your business with smart forms and say sayonara to
                  the busywork.
                </em>{' '}
                We’re talking forms so clever you can set your business on
                autopilot and go take that fishing trip you’ve been meaning to.
                Take payments. Automate workflows. Use question logic for
                tailored experiences. Make magic with calculations. Show us how
                you like the job done. We’ll take it from there.{' '}
              </span>
            }
          >
            <div>
              <img
                className="lozad"
                data-src="/website/images/logic-preview.png"
              />
              <img
                className="lozad hidden lg:block"
                data-src="/website/images/calculation-preview.png"
              />
              <img
                className="lozad hidden lg:block"
                data-src="/website/images/scoring-preview.png"
              />
            </div>
          </ContentBlock>
          <ContentBlock
            linkURL="/templates"
            linkLabel="See all templates"
            title="The only limit is your imagination"
            description={
              <>
                <span>
                  <em>Create your own unique designs.</em> Don’t settle for
                  another basic form that looks like everyone else’s. Make it
                  yours. Get creative with built-in Unsplash and GIPHY
                  libraries, or use our in-app image editor to add finishing
                  touches on the fly. Curate your unique look and feel by
                  customizing colors, fonts, layouts and themes, or go crazy
                  with Emojis. Less drab, more fab. ✨
                </span>
                <br />
                <br />
                <span>
                  <em>Choose from hundreds of free, pre-built templates</em> to
                  inspire your work. Get a headstart on bringing your dreams to
                  life.
                </span>
              </>
            }
          >
            <video
              autoPlay
              loop
              muted
              disableRemotePlayback="true"
              playsInline
              style={{ aspectRatio: '1' }}
              className="w-full max-w-full bg-white my-6 lozad "
            >
              <source data-src="/website/video/templates-smaller.mp4" />
            </video>
          </ContentBlock>

          <ContentBlock
            align="right"
            title="Scheduling as easy as 12? 2? 3?"
            description={
              <span>
                <em>Stay on schedule without the back-and-forth emails</em>. You
                spend enough time in meetings, you don’t want to spend even more
                time scheduling them. Just block out times and Paperform will
                look after the rest—all synced with Google Calendar. Eliminate
                manual follow-ups for good. All you need to do is turn up.{' '}
              </span>
            }
          >
            <img
              data-src="/website/images/appointments.jpg"
              style={{ aspectRatio: '99 / 95' }}
              alt="Appointments"
              className="w-full max-w-full lozad"
            />
          </ContentBlock>

          <ContentBlock
            title="Understand what’s working"
            description={
              <span>
                <em>Turn insights into actions with built-in analytics</em>. Get
                all the info you need to improve performance and analyze your
                customers. See what’s working at a glance with data
                visualizations, and generate custom reports to share among your
                team. Prefer to use Google Analytics? Facebook Pixel? Your data
                is in your hands.
              </span>
            }
          >
            <AnalyticsAnimation />
          </ContentBlock>
          <ContentBlock
            title="Plays nicely with other tools"
            linkURL="/integrations"
            linkLabel="See all integrations"
            align="right"
            description={
              <span>
                <em>
                  Paperform connects to thousands of popular apps and services.
                </em>{' '}
                Sync data to Google Sheets. Share responses to Notion databases.
                Add Asana tasks. Bring all your work under one roof. It’s as
                quick and easy as a message between friends.{' '}
              </span>
            }
          >
            <img
              data-src="/website/images/integrations.jpg"
              style={{ aspectRatio: '1' }}
              className="lozad w-[600px] max-w-full mx-auto"
            />
          </ContentBlock>
        </div>

        <div className="bg-[#092d46] text-white">
          <div className="max-width mx-auto">
            <div className="py-[90px]">
              <div className="lg:grid lg:grid-cols-2 lg:gap-16 justify-center items-center">
                <div style={{ aspectRatio: '1/1', transform: 'scale(.9)' }}>
                  <Lottie animationData={homepage} loop />
                </div>
                <div>
                  <div className="w-[550px] max-w-full">
                    <h2 className="mb-6 text-4xl lg:w-[400px]">
                      Your digital Swiss Army Knife.
                    </h2>

                    <h3 className="text-xl mb-6">
                      Paperform does everything short of open bottles.
                    </h3>
                    <p className="text-sm mb-[50px] lg:mb-0">
                      You won’t believe all the things you can create. Forms
                      that collect information. Forms that keep you on schedule.
                      Forms that get you paid. That onboard and organize.
                      Educate and entertain. Persuade, sell and solve problems.
                      There’s truly a form for any situation. If you can dream
                      it, you can Paperform it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-stone  bg-[url(/website/logo-v3/graphics/1-4.svg)] bg-cover bg-center">
          <div className="max-width mx-auto">
            <div className="py-24 pb-56">
              <FadeIn>
                <div className="w-[500px] max-w-full">
                  <h2 className="text-4xl">Looking for specific features?</h2>
                  <Button sm black solid to="/pricing" className="mb-3 !mr-1">
                    See a full overview
                  </Button>
                  <Button sm yellow solid to="/help" className="mb-3 !mr-1">
                    Check out the help center
                  </Button>
                  <div className="text-sm opacity-50">
                    Or just ask us on live chat down the bottom right.
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>

        <div
          className="bg-white max-w-full text-center overflow-hidden py-24"
          style={{ backgroundSize: 'cover', backgroundPosition: 'center' }}
        >
          <h2 className="text-center text-3xl mb-12">
            We're in good form...
            <br /> just ask our customers
          </h2>
          <FadeIn>
            <ContentRotator interval={10}>
              {quotes.map((quote, i) => (
                <div
                  key={i}
                  className="p-[25px] flex flex-col h-full w-full lg:p-[50px] lg:px-[65px] lg:mx-3  max-w-full bg-dusk-light lg:rounded-[8px] shadow"
                >
                  <div className="text-sm mb-6">“{quote.quote}”</div>
                  <div
                    className="w-[82px] h-[82px] mb-3 mt-auto rounded-full bg-black mx-auto bg-cover bg-center lozad"
                    data-background-image={quote.avatar}
                  />
                  <div className="text-sm !mb-0">
                    {quote.name}
                    <br />
                    <span className="text-xs">{quote.business}</span>
                  </div>
                </div>
              ))}
            </ContentRotator>
          </FadeIn>
        </div>

        <div className="bg-gold justify-center bg-[url(/website/logo-v3/graphics/4-5.svg)] bg-cover bg-center">
          <div className="max-width mx-auto">
            <div className="py-16 lg:p-16 lg:py-24">
              <FadeIn>
                <div className="w-[350px] max-w-full">
                  <h2 className="text-4xl">Form a better life now.</h2>
                  <Button solid cloud lg href="/register">
                    Get your 7 day unrestricted trial
                  </Button>
                  <div className="text-xs my-3 opacity-75">
                    No credit card needed.
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
        <div className="bg-stone">
          <div className="max-width mx-auto">
            <div className="py-6 pt-12">
              <div className="text-lg font-sonoma font-regular mb-6 w-[400px] max-w-full">
                Trusted By Thousands of Organizations Across the Globe.
              </div>
              <img
                data-src="/website/images/customers.png"
                className="m-0 mx-auto lozad"
                style={{ aspectRatio: '528 / 25' }}
              />
            </div>
          </div>
        </div>
      </DefaultLayout>
    </div>
  );
}
